import {
  Badge,
  Box,
  ButtonGroup,
  Flex,
  Icon,
  IconButton,
  Spinner,
  Table,
  TableProps,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { RiCheckboxCircleLine, RiCloseCircleLine } from 'react-icons/ri';

import { useAuth } from '../../../../../../../../../../../../../../../../../hooks/auth';
import { IBookingSessionBlockBookingBase } from '../../../../../../../../../../../../../../../../../models/bookings';
import { confirmSessionBlockBookingsService } from '../../../../../../../../../../../../../../../../../services/Activities/ConfirmSessionBlockBookingsService';
import { declineSessionBlockBookingsService } from '../../../../../../../../../../../../../../../../../services/Activities/DeclineSessionBlockBookingsService';
import {
  IAffectedSessionBlockReservation,
  listAffectedSessionBlockReservationsService,
} from '../../../../../../../../../../../../../../../../../services/Activities/ListAffectedSessionBlockReservationsService';
import { translateError } from '../../../../../../../../../../../../../../../../../utils/errors';
import { maskDateTime } from '../../../../../../../../../../../../../../../../../utils/formatters/handleMask';

const sessionBlockBookingStatusLabels: Record<string, string> = {
  CONFIRMED_BY_USER: 'Confirmada',
  DECLINED_BY_USER: 'Recusada',
  MANUAL_HANDLING: 'Em atendimento',
  PENDING_CONFIRMATION: 'Aguardando confirmação',
  RELEASED: 'Fila liberada',
  SUSPENDED: 'Sessão paralisada',
};

const sessionBlockBookingStatusColorSchemes: Record<string, string> = {
  CONFIRMED_BY_USER: 'green',
  DECLINED_BY_USER: 'red',
  MANUAL_HANDLING: 'orange',
  PENDING_CONFIRMATION: 'yellow',
  RELEASED: 'green',
  SUSPENDED: 'purple',
};

interface IScheduleSessionBlocksTableProps extends TableProps {
  activityId: string;
  bookedDate: string;
  scheduleIds: string[];
}

export const ScheduleSessionBlocksTable = ({
  activityId,
  bookedDate,
  scheduleIds,
  ...rest
}: IScheduleSessionBlocksTableProps): JSX.Element => {
  const toast = useToast();
  const { experienceLevelsTitleMap } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [reservations, setReservations] = useState<
    IAffectedSessionBlockReservation[]
  >([]);

  const scheduleIdsSet = useMemo(() => new Set(scheduleIds), [scheduleIds]);

  const loadReservations = useCallback(async () => {
    try {
      setIsLoading(true);

      const { items } = await listAffectedSessionBlockReservationsService({
        activityId,
      });

      setReservations(
        items.filter(
          (reservation) =>
            reservation.reservation.bookedDate === bookedDate &&
            scheduleIdsSet.has(reservation.schedule.id),
        ),
      );
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status !== 401) {
        toast({
          title: 'Falha ao carregar paralisações',
          description:
            translateError({ message: err.response?.data.message }) ||
            'Ocorreu um erro ao carregar as paralisações da sessão.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [activityId, bookedDate, scheduleIdsSet, toast]);

  useEffect(() => {
    loadReservations();
  }, [loadReservations]);

  const handleUpdateReservation = useCallback(
    (
      reservationId: string,
      sessionBlockBooking: IBookingSessionBlockBookingBase,
    ) => {
      setReservations((prevState) =>
        prevState.map((reservation) =>
          reservation.reservation.id === reservationId
            ? {
                ...reservation,
                impactStatus: sessionBlockBooking.status,
                reservation: {
                  ...reservation.reservation,
                  deletedAt:
                    sessionBlockBooking.status === 'DECLINED_BY_USER'
                      ? new Date().toISOString()
                      : reservation.reservation.deletedAt,
                  status: sessionBlockBooking.status,
                },
              }
            : reservation,
        ),
      );
    },
    [],
  );

  const notifySessionBlocksChanged = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('activity-session-blocks:changed', {
        detail: { activityId },
      }),
    );
  }, [activityId]);

  const handleConfirmReservation = useCallback(
    async (reservation: IAffectedSessionBlockReservation) => {
      try {
        const updatedSessionBlockBooking =
          await confirmSessionBlockBookingsService({
            blockId: reservation.block.id,
            bookingId: reservation.reservation.id,
          });

        handleUpdateReservation(
          reservation.reservation.id,
          updatedSessionBlockBooking,
        );
        notifySessionBlocksChanged();

        toast({
          title: 'Reserva confirmada',
          description: 'A reserva foi confirmada corretamente.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao confirmar',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao confirmar a reserva.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [handleUpdateReservation, notifySessionBlocksChanged, toast],
  );

  const handleDeclineReservation = useCallback(
    async (reservation: IAffectedSessionBlockReservation) => {
      try {
        const updatedSessionBlockBooking =
          await declineSessionBlockBookingsService({
            blockId: reservation.block.id,
            bookingId: reservation.reservation.id,
          });

        handleUpdateReservation(
          reservation.reservation.id,
          updatedSessionBlockBooking,
        );
        notifySessionBlocksChanged();

        toast({
          title: 'Reserva recusada',
          description: 'A reserva foi cancelada sem prejuízo.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          variant: 'subtle',
          position: 'top-right',
        });
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status !== 401) {
          toast({
            title: 'Falha ao recusar',
            description:
              translateError({ message: err.response?.data.message }) ||
              'Ocorreu um erro ao recusar a reserva.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            variant: 'subtle',
            position: 'top-right',
          });
        }
      }
    },
    [handleUpdateReservation, notifySessionBlocksChanged, toast],
  );

  if (isLoading) {
    return (
      <Flex w="full" h="24" justify="center" align="center">
        <Spinner />
      </Flex>
    );
  }

  if (!reservations.length) {
    return (
      <Flex w="full" h="24" justify="center" align="center">
        <Text color="gray.600">Nenhuma paralisação para essa sessão</Text>
      </Flex>
    );
  }

  return (
    <Table colorScheme="blue" {...rest}>
      <Thead>
        <Tr>
          <Th>Nome</Th>
          <Th>Nível reservado</Th>
          <Th>Tipo</Th>
          <Th>Status</Th>
          <Th>Criado em</Th>
          <Th textAlign="right" w="8" />
        </Tr>
      </Thead>

      <Tbody>
        {reservations.map((reservation) => (
          <Tr key={`${reservation.type}-${reservation.id}`}>
            <Td>
              <Box>
                <Text fontWeight="bold">{reservation.user.name}</Text>
                <Text textColor="gray.400" fontSize="sm">
                  {reservation.type === 'BOOKING' ? 'Reserva' : 'Fila'}
                </Text>
              </Box>
            </Td>

            <Td>
              {experienceLevelsTitleMap.get(
                reservation.schedule.minUserExperience,
              )}
            </Td>

            <Td>
              <Badge
                colorScheme={reservation.type === 'BOOKING' ? 'blue' : 'cyan'}
              >
                {reservation.type === 'BOOKING' ? 'Reserva' : 'Fila'}
              </Badge>
            </Td>

            <Td>
              <Badge
                colorScheme={
                  sessionBlockBookingStatusColorSchemes[
                    reservation.impactStatus
                  ]
                }
              >
                {sessionBlockBookingStatusLabels[reservation.impactStatus]}
              </Badge>
            </Td>

            <Td>{maskDateTime(reservation.reservation.createdAt)}</Td>

            <Td textAlign="right">
              {reservation.type === 'BOOKING' &&
                reservation.impactStatus === 'PENDING_CONFIRMATION' && (
                  <ButtonGroup>
                    <IconButton
                      aria-label="confirm-session-block-booking"
                      size="sm"
                      icon={<Icon as={RiCheckboxCircleLine} fontSize="16" />}
                      colorScheme="green"
                      onClick={() => handleConfirmReservation(reservation)}
                    />

                    <IconButton
                      aria-label="decline-session-block-booking"
                      size="sm"
                      icon={<Icon as={RiCloseCircleLine} fontSize="16" />}
                      colorScheme="red"
                      onClick={() => handleDeclineReservation(reservation)}
                    />
                  </ButtonGroup>
                )}
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
};
